import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Content,
  PageContainer,
  TitleContainer,
  TitleRightSide,
  Input,
  RecentlyAddedButton,
} from './AgencyInfluencersPage.styles';
import { Text24Bold } from '../../../components/utils/Text/Text.styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DateRangePickerComponent, InfluencerItem, Select, Table } from '../../../components';
import { AGENCY_INFLUENCERS } from '../../../components/App/routes';
import { ORDER } from '../../../components/Table/Table';
import { fetchInfluencerAnalytics } from '../../../store/slices/agency/asyncThunks';
import { selectFetchInfluencerAnalyticsInfo } from '../../../store/slices/agency/slice';
import { EARNINGS_TYPE } from '../../../utils/constants';
import { selectDateRange, selectEarningType, setEarningType } from '../../../store/slices/user/slice';
import debounce from 'lodash.debounce';

const RECENTLY_ADDED_SORT_KEY = 'createdAt';

const AgencyInfluencersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = +searchParams.get('page') || 1;
  const search = searchParams.get('search');
  const [searchValue, setSearchValue] = useState(() => search ?? '');
  const range = useSelector(selectDateRange);
  const earningType = useSelector(selectEarningType);
  const { data, total, pending } = useSelector(selectFetchInfluencerAnalyticsInfo);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [sortOptions, setSortOptions] = useState({
    sortBy: '',
    order: ORDER.DESC,
  });

  const changeHandler = useCallback(
    text => {
      searchParams.set('search', text);
      searchParams.set('page', 1);
      setSearchParams(searchParams);
    },
    [setSearchParams, searchParams],
  );

  const debouncedChangeHandler = useMemo(() => debounce(changeHandler, 300), [changeHandler]);

  useEffect(() => {
    if (page > 0) {
      dispatch(
        fetchInfluencerAnalytics({
          page,
          search: search ?? '',
          startDate: range[0],
          endDate: range[1],
          sortBy: sortOptions.sortBy,
          order: sortOptions.order,
          earningType: earningType.value,
        }),
      );
    }
  }, [dispatch, page, sortOptions, range, earningType, search]);

  const setPage = page => {
    searchParams.set('page', page);
    setSearchParams(searchParams);
  };

  const isRecentlyAdded = sortOptions.sortBy === RECENTLY_ADDED_SORT_KEY;

  const toggleRecentlyAdded = () => {
    setSortOptions({ sortBy: isRecentlyAdded ? '' : RECENTLY_ADDED_SORT_KEY, order: ORDER.DESC });
    setPage(1);
  };

  const tableHeader = [
    { label: 'Name' },
    { label: 'Content Earnings', sortable: true, key: 'totalContentEarnings' },
    { label: 'Other Earnings', sortable: true, key: 'totalOtherEarnings' },
    { label: 'Total Earnings', sortable: true, key: 'totalEarnings' },
    { type: 'action' },
  ];

  return (
    <PageContainer>
      <TitleContainer>
        <Text24Bold>Creators</Text24Bold>
        <TitleRightSide>
          <RecentlyAddedButton type="button" $active={isRecentlyAdded} onClick={toggleRecentlyAdded}>
            Recently added
          </RecentlyAddedButton>
          <Input
            hideErrorMessage
            placeholder="Search"
            value={searchValue}
            onChange={event => {
              setSearchValue(event.target.value);
              debouncedChangeHandler(event.target.value);
            }}
          />
          <DateRangePickerComponent />
          <Select
            options={[
              { value: EARNINGS_TYPE.GROSS, label: 'Gross' },
              { value: EARNINGS_TYPE.NET, label: 'Net' },
            ]}
            handleChange={value => dispatch(setEarningType(value))}
            selectedOption={earningType}
            hideError
          />
        </TitleRightSide>
      </TitleContainer>
      <Content>
        <Table
          headerInformation={tableHeader}
          dataLoading={pending}
          totalCount={total}
          currentPage={page}
          perPage={10}
          setPage={setPage}
          emptyDataText={'No data'}
          sortOptions={sortOptions}
          setSortOptions={setSortOptions}
          fullHeight>
          {data.map(item => (
            <InfluencerItem
              key={item.influencer}
              name={item.influencerName}
              profilePhoto={item.influencerPhoto}
              deleted={!!item.deletedAt}
              totalOtherEarnings={item.totalOtherEarnings}
              totalContentEarnings={item.totalContentEarnings}
              totalEarnings={item.totalEarnings}
              onDetailsClicked={() => navigate(`/${AGENCY_INFLUENCERS}/${item.influencer}`)}
            />
          ))}
        </Table>
      </Content>
    </PageContainer>
  );
};

export default AgencyInfluencersPage;
